import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import Cart from './components/Cart';
import ScrollToTop from './components/ScrollToTop';
import PrivateRoute from './components/PrivateRoute';
import AppChoicePopup from './components/AppChoicePopup';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import ProfilePage from './pages/ProfilePage';
import WishlistPage from './pages/WishlistPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import "./app.css";

const theme = createTheme({
  palette: {
    primary: {
      main: '#C9A961',
      light: '#D4B876',
      dark: '#B89850',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1A1A1A',
      light: '#6B6B6B',
      dark: '#000000',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F5F2EB',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#6B6B6B',
    },
  },
  typography: {
    fontFamily: "'Montserrat', sans-serif",
    h1: {
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 700,
      letterSpacing: '0.05em',
    },
    h2: {
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 600,
    },
    h3: {
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 600,
    },
    h4: {
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 600,
    },
    h5: {
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 500,
    },
    h6: {
      fontFamily: "'Cormorant Garamond', serif",
      fontWeight: 500,
    },
    button: {
      fontFamily: "'Montserrat', sans-serif",
      fontWeight: 600,
      letterSpacing: '0.05em',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 4px rgba(201, 169, 97, 0.1)',
    '0px 4px 8px rgba(201, 169, 97, 0.15)',
    '0px 8px 16px rgba(201, 169, 97, 0.2)',
    '0px 12px 24px rgba(201, 169, 97, 0.25)',
    '0px 16px 32px rgba(201, 169, 97, 0.3)',
    '0px 20px 40px rgba(201, 169, 97, 0.35)',
    '0px 24px 48px rgba(201, 169, 97, 0.4)',
    ...Array(17).fill('none'),
  ],
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <CurrencyProvider>
          <CartProvider>
            <Router>
              <ScrollToTop />
              <CssBaseline />
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <Navigation />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/shop" element={<ShopPage />} />
                  <Route path="/products/:slug" element={<ProductDetailPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
                  <Route path="/wishlist" element={<PrivateRoute><WishlistPage /></PrivateRoute>} />
                  <Route path="/orders" element={<PrivateRoute><OrdersPage /></PrivateRoute>} />
                  <Route path="/orders/:id" element={<PrivateRoute><OrderDetailPage /></PrivateRoute>} />
                </Routes>
                <Footer />
                <Cart />
                <AppChoicePopup />
              </Box>
            </Router>
          </CartProvider>
        </CurrencyProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
